import type { Action } from '@0xtorch/core'
import type { BlockEvent } from '../../types'
import { parseAddressToOtherChain } from '../../utils'
import { createBalancesBurnedTransfer } from './createTransfer'
import { balancesBurnedSchema, xcmPalletFeesPaidSchema } from './schemas'
import type { HandleEventParameters, HandleEventReturnType } from './types'

type FeeRequest = {
  eventIndex: number
  payer: string
  amount: bigint
}

type BurnedEntry = {
  eventIndex: number
  payer: string
  amount: bigint
}

/** xcmPallet.FeesPaid に対応する balances.Burned を fee action へ変換する。 */
export const convertXcmFeesPaid = ({
  chain,
  timestamp,
  source,
  events,
  relatedAddresses,
  debug,
}: HandleEventParameters): HandleEventReturnType => {
  const feeRequests: FeeRequest[] = []
  const burnedEntries: BurnedEntry[] = []
  for (const [eventIndex, event] of events.entries()) {
    if (
      (event.section === 'xcmPallet' || event.section === 'xcmpallet') &&
      event.method === 'FeesPaid'
    ) {
      const parsed = xcmPalletFeesPaidSchema.safeParse(event.data)
      if (parsed.success) {
        const payer = parseAddressToOtherChain({
          address: parsed.data.paying.interior.X1[0].AccountId32.id,
          toChain: chain,
          debug,
        })
        if (payer === undefined || !relatedAddresses.has(payer)) {
          continue
        }
        let feeAmount = 0n
        for (const fee of parsed.data.fees) {
          if (fee.id.parents === 0n && fee.id.interior === 'Here') {
            feeAmount += fee.fun.Fungible
          }
        }
        if (feeAmount > 0n) {
          feeRequests.push({ eventIndex, payer, amount: feeAmount })
        }
      }
    }
    if (event.section === 'balances' && event.method === 'Burned') {
      const parsed = balancesBurnedSchema.safeParse(event.data)
      if (parsed.success && relatedAddresses.has(parsed.data.who)) {
        burnedEntries.push({
          eventIndex,
          payer: parsed.data.who,
          amount: parsed.data.amount,
        })
      }
    }
  }

  const usedAmountsByBurnedIndex = new Map<number, bigint>()
  for (const feeRequest of feeRequests) {
    const candidates = burnedEntries.filter(
      (entry) =>
        entry.eventIndex < feeRequest.eventIndex &&
        entry.payer === feeRequest.payer &&
        entry.amount > (usedAmountsByBurnedIndex.get(entry.eventIndex) ?? 0n),
    )
    const exactCandidate = candidates.findLast(
      (entry) =>
        entry.amount -
          (usedAmountsByBurnedIndex.get(entry.eventIndex) ?? 0n) ===
        feeRequest.amount,
    )
    if (exactCandidate !== undefined) {
      usedAmountsByBurnedIndex.set(
        exactCandidate.eventIndex,
        exactCandidate.amount,
      )
      continue
    }

    const availableAmount = candidates.reduce(
      (sum, entry) =>
        sum +
        entry.amount -
        (usedAmountsByBurnedIndex.get(entry.eventIndex) ?? 0n),
      0n,
    )
    if (availableAmount < feeRequest.amount) {
      continue
    }

    let remainingFeeAmount = feeRequest.amount
    for (const entry of candidates.toReversed()) {
      const usedAmount = usedAmountsByBurnedIndex.get(entry.eventIndex) ?? 0n
      const availableBurnedAmount = entry.amount - usedAmount
      const allocatedAmount =
        availableBurnedAmount < remainingFeeAmount
          ? availableBurnedAmount
          : remainingFeeAmount
      usedAmountsByBurnedIndex.set(
        entry.eventIndex,
        usedAmount + allocatedAmount,
      )
      remainingFeeAmount -= allocatedAmount
      if (remainingFeeAmount === 0n) {
        break
      }
    }
  }
  if (usedAmountsByBurnedIndex.size === 0) {
    return undefined
  }

  const transfersByPayer = new Map<string, Action['transfers']>()
  const unhandledEvents: BlockEvent[] = []
  for (const [eventIndex, event] of events.entries()) {
    if (event.section !== 'balances' || event.method !== 'Burned') {
      unhandledEvents.push(event)
      continue
    }

    const parsed = balancesBurnedSchema.safeParse(event.data)
    if (!parsed.success || !relatedAddresses.has(parsed.data.who)) {
      unhandledEvents.push(event)
      continue
    }
    const usedAmount = usedAmountsByBurnedIndex.get(eventIndex)
    if (usedAmount === undefined) {
      unhandledEvents.push(event)
      continue
    }
    const transfer = createBalancesBurnedTransfer({
      chain,
      data: { who: parsed.data.who, amount: usedAmount.toString() },
      relatedAddresses,
      debug,
    })
    if (transfer === undefined) {
      return undefined
    }
    transfersByPayer.set(parsed.data.who, [
      ...(transfersByPayer.get(parsed.data.who) ?? []),
      transfer,
    ])

    const unhandledAmount = parsed.data.amount - usedAmount
    if (unhandledAmount > 0n) {
      unhandledEvents.push({
        ...event,
        data: { who: parsed.data.who, amount: unhandledAmount.toString() },
      })
    }
  }

  return {
    actions: [...transfersByPayer.values()].map(
      (transfers): Action => ({
        type: 'fee',
        timestamp,
        source,
        order: 0,
        evidence: 'contract',
        transfers,
      }),
    ),
    unhandledEvents,
  }
}
